<template>
	<BasePlaceHolder
		:line-count="4"
		:section-count="4"
		class="container pt-300 pb-300"
		:placehold="loading"
		:theme="placeholdTheme"
	>
		<article>
			<div class="grid">
				<header class="col-12 col-10-md col-offset-1-md col-8-lg col-offset-2-lg">
					<BaseAlert v-if="articleAlert" class="mb-300" :alert="articleAlert" data-test="article-alert" />
					<p v-if="isVcxArticle" class="overline text-color-gray-40" data-test="article-breadcrumbs-static"
						>Press Release</p
					>
					<p v-else class="overline" data-test="article-breadcrumbs">
						<BaseLink :link="blogLanding" classes="cta-link--rust article-category" />
						<BaseLink
							v-if="category"
							:link="category"
							classes="cta-link--rust article-category category-name"
						/>
					</p>
					<h1 class="heading-1 mb-100 mt-100">{{ article.title }}</h1>
					<p v-if="article?.subtitle" :class="isVcxArticle ? 'vcx-body-xl' : 'body-xl'">
						{{ article?.subtitle }}
					</p>
					<p class="blog-byline">
						<span
							data-test="article-author"
							:class="[
								'display-inline-block',
								{
									'text-transform-capitalize': article?.author?.name
								}
							]"
						>
							{{ author }}
						</span>
						<ArticleDate
							v-if="article?.date"
							class="bl ml-50 pl-50"
							:date-string="article?.date || article._createdAt"
							:format="CustomSpacetimeFormat.MONTH_DATE_PAD_YEAR"
						/>
					</p>
				</header>

				<figure
					v-if="!article.hideCoverImage && article.coverImage && article.coverImage.asset.url"
					class="ml-0 mt-150 mr-0 mb-0 col-12 col-offset-1-lg col-10-lg"
					role="presentation"
					data-test="article-cover-image-container"
				>
					<ArticleCoverImage :cover-image="article.coverImage.asset" />
				</figure>
			</div>

			<section class="display-flex display-block-lg grid-lg flex-wrap-wrap justify-content-center">
				<div class="col-12 col-10-md col-8-lg col-offset-2-lg article-body pt-200">
					<p
						v-if="article?.lead"
						class="article-lead mb-100"
						:class="isVcxArticle ? 'vcx-body-xl' : 'body-xl'"
					>
						{{ article.lead }}
					</p>

					<ArticleBodyContent v-if="bodyContent" :body-content="bodyContent" :key-takeaways="keyTakeaways" />
					<aside v-if="showCta" class="article-cta pl-100 pt-300 pr-100 pb-300 mt-150 mb-150 grid">
						<div class="col-12 col-8-md col-offset-2-md">
							<p class="article-cta-heading heading-6 mb-100">
								{{ article?.emailCapturePrompt ?? 'We make investing simple. Discover how.' }}
							</p>
							<LeadCaptureComponent class="mt-100" :form-config="leadCaptureFormConfig" />
						</div>
					</aside>
					<aside v-else-if="displayMicroFeedback" class="my-300">
						<div class="col-6-md">
							<TransitionCollapse>
								<div data-test="radio-feedback-wrapper">
									<span class="body-md font-weight-bold"
										>How would you rate your overall satisfaction with this experience?</span
									>
									<MicroFeedbackRadios @reaction-selected="reactionSelected" />
								</div>
							</TransitionCollapse>
						</div>
					</aside>
					<footer
						v-if="footnotes.length"
						class="article-disclaimers disclaimer mt-100 pt-100 pt-200-md mt-200-md"
					>
						<RichTextComponent :blocks="footnotes" />
					</footer>
				</div>
			</section>
		</article>
	</BasePlaceHolder>

	<section
		v-if="recommended.length > 0"
		class="article-list-recommended bg-color-greige pt-300 pb-300"
		:class="'recommended-count-' + recommended.length"
		data-test="recommended-articles-section"
	>
		<BasePlaceHolder :line-count="4" :placehold="loading" :theme="placeholdTheme" class="container">
			<h2 class="heading-5 mb-50">Recommended articles</h2>
			<div v-if="recommended.length > 0" class="display-flex flex-wrap-wrap gutters-300-md">
				<ArticleCard
					v-for="(article, articleId) in recommended"
					:key="articleId"
					:article="article"
					:base-link-classes="'article-list-item-inner'"
					:classes="recommendedColClass + ' col-12 col-6-md mt-100 mb-50'"
				/>
			</div>
		</BasePlaceHolder>
	</section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { trackContent } from '@api/content-track';
import { hasSubmittedMicrofeedback } from '@api/micro-feedback';
import ArticleBodyContent from '@components/blog/article-body-content.vue';
import ArticleCard from '@components/blog/article-card.vue';
import ArticleCoverImage from '@components/blog/article-cover-image.vue';
import ArticleDate from '@components/blog/article-date.vue';
import TransitionCollapse from '@components/layout/transition-collapse.vue';
import LeadCaptureComponent from '@components/lead-capture/lead-capture-form.vue';
import MicroFeedbackRadioFormModal from '@components/micro-feedback/micro-feedback-radio-form-modal.vue';
import MicroFeedbackRadios from '@components/micro-feedback/micro-feedback-radios.vue';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { VcxBlogCategorySlug } from '@constants/vcx';
import { useAppStore } from '@stores/app';
import { useMixpanel } from '@utils/composables/use-mixpanel';
import { BaseAlertComponentType, Link } from 'types/layout';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import { MicroFeedbackReactionType } from 'types/micro-feedback';
import { SanityRichTextBlock } from 'types/sanity';
import { SanityArticle, SanityArticleTeaser, SanityKeyTakeaway } from 'types/sanity/education-center';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';

interface Props {
	article: SanityArticle;
	loading: boolean;
	placeholdTheme?: string;
}

const props = defineProps<Props>();

const route = useRoute();

const appStore = useAppStore();

const blogLanding: Link = {
	text: 'Education',
	router: 'blog-landing'
};

const isVcxArticle = computed((): boolean => {
	return props.article?.category?.slug?.current === VcxBlogCategorySlug || route.name === 'vcx-blog-post';
});

const leadCaptureFormConfig: LeadCaptureFormConfig = {
	centerButton: true,
	placeholder: 'Enter your email',
	signupFlowType: 'STANDARD',
	thirdPartyAuthTheme: isVcxArticle.value ? 'SECONDARY' : 'PRIMARY'
};

const author = computed(() => {
	return `By ${props.article?.author?.name ?? 'the Fundrise Team'}`;
});

const category = computed<Link | null>(() => {
	if (props.article?.category?.name && props.article?.category?.slug) {
		return {
			text: props.article.category.name,
			router: 'blog-category',
			params: { categoryUrlSlug: props.article.category.slug.current }
		};
	}
	return null;
});

const articleAlert = computed((): BaseAlertComponentType | null => {
	if (!props.article?.customArticleAlert?.alertMessage || !props.article?.customArticleAlert?.showAlert) return null;

	const ctaLink: Link | undefined =
		props.article?.customArticleAlert?.alertCTA?.url && props.article?.customArticleAlert?.alertCTA?.linkText
			? {
					text: props.article.customArticleAlert.alertCTA.linkText,
					href: props.article.customArticleAlert.alertCTA.url,
					target: props.article.customArticleAlert.alertCTA.openInNewTab ? '_blank' : ''
				}
			: undefined;

	const alertMessage: Array<string> = props.article?.customArticleAlert?.alertMessage.split('\n') ?? [];

	return {
		type: props.article?.customArticleAlert?.alertType ?? 'info',
		heading: props.article?.customArticleAlert?.alertHeading ?? undefined,
		message: alertMessage,
		cta: ctaLink
	};
});

const bodyContent = computed((): Array<SanityRichTextBlock> => {
	return props.article?.body ?? [];
});

const footnotes = computed((): Array<SanityRichTextBlock> => {
	return props.article?.footnotes ?? [];
});

const keyTakeaways = computed((): Array<SanityKeyTakeaway> => {
	return props.article?.keyTakeawaysSection ?? [];
});

const recommended = computed<Array<SanityArticleTeaser>>(() => {
	return props.article?.recommended ?? [];
});

const urlSlug = computed((): string => {
	return props.article.slug.current;
});

const displayMicroFeedback = ref(false);

const showCta = computed((): boolean => {
	return !!props.article?.addEmailCaptureCta && !appStore.isAuthenticated;
});

const recommendedColClass = computed((): string => {
	if (recommended.value.length < 3) {
		return 'col-6-lg';
	} else if (recommended.value.length === 3) {
		return 'col-4-lg';
	}
	return 'col-3-lg';
});

const { trackMixpanelEvent } = useMixpanel();

const reaction = ref<MicroFeedbackReactionType | null>(null);

function reactionSelected(selectedReaction: MicroFeedbackReactionType): void {
	const mixpanelData = {
		'Entry Point': 'Education Article',
		'Event Type': 'EDUCATION_ARTICLE',
		'Associated Event ID': urlSlug.value,
		'Satisfaction Response': selectedReaction
	};
	trackMixpanelEvent('Rating clicked', mixpanelData);
	reaction.value = selectedReaction;
	openMicroFeedbackModal();
}

function openMicroFeedbackModal(): void {
	appStore.updateCurrentModal({
		modal: MicroFeedbackRadioFormModal,
		props: {
			microFeedbackEventType: 'EDUCATION_ARTICLE',
			associatedEventId: urlSlug.value,
			preSelectedResponse: reaction.value
		}
	});
	displayMicroFeedback.value = false;
}

async function setup(): Promise<void> {
	if (appStore.isAuthenticated && props.article && props.article.display_microfeedback) {
		trackContent(urlSlug.value, 'ARTICLE');
		if (props.article.display_microfeedback) {
			const submittedMicrofeedback = await hasSubmittedMicrofeedback('EDUCATION_ARTICLE', urlSlug.value);
			displayMicroFeedback.value = !submittedMicrofeedback;
		}
	}
}

setup();
</script>
